import { useEffect, useMemo, useState } from "react";

import { ensureCsrfToken } from "../../../api";

import Swal from "sweetalert2";

const HEADER_FIELDS = [
  
  
  {
    key: "wholdtax_master_header",
    label: "Upload WithholdTax Master Header Excel",
    help: "Supports withholding tax field mapping.",
  },
  {
    key: "gsttax_master_header",
    label: "Upload GSTTax Master Header Excel",
    help: "Required for GST tax code mapping and validations.",
  }
];

function createInitialFiles() {
  return HEADER_FIELDS.reduce((accumulator, field) => {
    accumulator[field.key] = null;
    return accumulator;
  }, {});
}

export default function UploadHeadersView() {
  const [files, setFiles] = useState(createInitialFiles);
  const [uploadStatus, setUploadStatus] = useState({});
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const selectedCount = useMemo(
    () => Object.values(files).filter(Boolean).length,
    [files],
  );

  const uploadedCount = useMemo(
    () => Object.values(uploadStatus || {}).filter(Boolean).length,
    [uploadStatus],
  );

  useEffect(() => {
    let cancelled = false;

    async function loadUploadStatus() {
      setLoadingStatus(true);
      try {
        const response = await fetch("/api/upload-headers/status/", {
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        });
        const payload = await response.json();

        if (!response.ok) {
          throw new Error(payload.message || "Unable to load header upload status.");
        }

        if (!cancelled) {
          setUploadStatus(payload.status || {});
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(requestError.message || "Unable to load header upload status.");
        }
      } finally {
        if (!cancelled) {
          setLoadingStatus(false);
        }
      }
    }

    loadUploadStatus();

    return () => {
      cancelled = true;
    };
  }, []);

  function handleFileChange(fieldKey, event) {
    const file = event.target.files?.[0] || null;
    setFiles((current) => ({
      ...current,
      [fieldKey]: file,
    }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setSubmitting(true);
    setStatus("");
    setError("");

    if (!selectedCount) {
      setSubmitting(false);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Select at least one header file before uploading.",
        confirmButtonText: "OK",
      });
      return;
    }

    try {
      const csrfToken = await ensureCsrfToken();
      const formData = new FormData();

      HEADER_FIELDS.forEach((field) => {
        if (files[field.key]) {
          formData.append(field.key, files[field.key]);
        }
      });

      const response = await fetch("/api/upload-headers/", {
        method: "POST",
        credentials: "include",
        headers: {
          "X-CSRFToken": csrfToken,
        },
        body: formData,
      });

      const payload = await response.json();

      if (!response.ok || payload.status !== "success") {
        
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Unable to upload header files.",
          confirmButtonText: "OK",
        });
      }

      Swal.fire({
        icon: "success",
        title: "Success",
        text: payload.message || "Headers uploaded successfully.",
        confirmButtonText: "OK",
      });
      setUploadStatus(payload.uploadStatus || {});
      setFiles(createInitialFiles());
    } catch (requestError) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Upload failed. Try again.",
        confirmButtonText: "OK",
      });
      
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="upload-view">
      <div className="workspace-panel upload-view__hero">
        <div>
          <p className="eyebrow">Header Library</p>
          <h2>Upload Source Headers</h2>
          <p className="config-view__intro">
            Manage uploaded header definitions by source type. Re-uploading a type will replace its
            saved header set for the current company.
          </p>
        </div>
        <div className="upload-view__summary">
          <span className="upload-view__summary-count">{loadingStatus ? "..." : uploadedCount}</span>
          <span className="upload-view__summary-label">header types uploaded</span>
        </div>
      </div>

      {status ? <div className="config-banner config-banner--success">{status}</div> : null}
      {error ? <div className="config-banner config-banner--error">{error}</div> : null}

      <form className="upload-grid" onSubmit={handleSubmit}>
        {HEADER_FIELDS.map((field) => (
          <article key={field.key} className="upload-card">
            <div className="upload-card__head">
              <div className="upload-card__title-row">
                <h3>{field.label}</h3>
                <span className={`pill${uploadStatus[field.key] ? "" : " pill--muted"}`}>
                  {uploadStatus[field.key] ? "Uploaded" : "Not uploaded"}
                </span>
              </div>
              <p>{field.help}</p>
            </div>

            <label className="upload-card__input">
              <span>Select Excel file</span>
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(event) => handleFileChange(field.key, event)}
              />
            </label>

            <div className="upload-card__meta">
              {files[field.key] ? files[field.key].name : "No file selected"}
            </div>

            <p className="config-note">
              {uploadStatus[field.key]
                ? "A header set is already saved for this type. Upload again to replace it."
                : "No saved header set found yet for this type."}
            </p>
          </article>
        ))}

        <div className="workspace-panel upload-view__actions">
          <div>
            <p className="eyebrow">Submit</p>
            <h2>Upload header set</h2>
            <p className="config-note">
              You can upload one file or several in the same request. Existing saved header sets for
              the company will be updated by type.
            </p>
          </div>

          <button className="primary-button" type="submit" disabled={submitting}>
            {submitting ? "Uploading..." : "Upload Headers"}
          </button>
        </div>
      </form>
    </section>
  );
}
